import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

const CarFinancialDetailCards = ({ cars }) => {
    const [expandedCard, setExpandedCard] = useState(null);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            PROCESSED: 'bg-blue-100 text-blue-800 border-blue-300',
            APPROVED: 'bg-green-100 text-green-800 border-green-300',
            PAID: 'bg-purple-100 text-purple-800 border-purple-300'
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    const toggleCard = (plateNo) => {
        setExpandedCard(expandedCard === plateNo ? null : plateNo);
    };

    if (!cars || cars.length === 0) {
        return <div className="text-center text-gray-500">No vehicles found</div>;
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {cars.map((car) => (
                <div key={car.plate_no} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-[#FF5733] to-[#e64520] p-4 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-bold">{car.plate_no}</h3>
                                <p className="text-sm opacity-90">{car.make} {car.model} {car.year}</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${getStatusColor(car.status)}`}>
                                {car.status}
                            </div>
                        </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-4">
                        {/* Key Metrics Grid */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <MetricBox label="Working Days" value={car.working_days || 6} isNumber />
                            <MetricBox label="Prev Balance" value={formatCurrency(car.prev_balance || 0)} />
                            <MetricBox label="Week Target" value={formatCurrency(car.week_target || 0)} />
                            <MetricBox label="WKI Total" value={formatCurrency(car.wki_total || 0)} />
                            <MetricBox label="WKR Total" value={formatCurrency(car.wkr_total || 0)} highlight />
                            <MetricBox label="Losses" value={formatCurrency(car.losses_total || 0)} danger />
                        </div>

                        {/* Financial Summary */}
                        <div className="space-y-2 bg-gray-50 rounded-lg p-3 mb-4">
                            <SummaryRow label="Adjustments" value={formatCurrency(car.adjustments_total || 0)} />
                            <SummaryRow label="Expenses" value={formatCurrency(car.expenses_total || 0)} />
                            <SummaryRow label="Total Charges" value={formatCurrency(car.total_charges || 0)} bold />
                            <div className="border-t border-gray-300 my-2"></div>
                            <SummaryRow label="Net Income" value={formatCurrency(car.net_income || 0)} bold highlight />
                            <SummaryRow label="Owner Payout" value={formatCurrency(car.owner_payout || 0)} bold success />
                            <SummaryRow label="Closing Balance" value={formatCurrency(car.closing_balance_after_payout || 0)} />
                        </div>

                        {/* Expand Button */}
                        <button
                            onClick={() => toggleCard(car.plate_no)}
                            className="w-full flex items-center justify-center gap-2 py-2 bg-slate-100 hover:bg-slate-200 rounded-sm transition-colors"
                        >
                            <span className="text-sm font-semibold text-gray-700">
                                {expandedCard === car.plate_no ? 'Hide' : 'Show'} Transaction Details
                            </span>
                            {expandedCard === car.plate_no ? (
                                <ChevronUpIcon className="w-4 h-4" />
                            ) : (
                                <ChevronDownIcon className="w-4 h-4" />
                            )}
                        </button>

                        {/* Expanded Transaction Details */}
                        {expandedCard === car.plate_no && (
                            <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                                {car.transactions?.remittance?.length > 0 && (
                                    <TransactionSection
                                        title="Remittance Transactions (WKR)"
                                        transactions={car.transactions.remittance}
                                    />
                                )}
                                {car.transactions?.losses?.length > 0 && (
                                    <TransactionSection
                                        title="Losses (LSS)"
                                        transactions={car.transactions.losses}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

const MetricBox = ({ label, value, isNumber, highlight, danger }) => {
    let bgColor = 'bg-white';
    if (highlight) bgColor = 'bg-green-50';
    if (danger) bgColor = 'bg-red-50';

    return (
        <div className={`${bgColor} border border-gray-200 rounded-lg p-2`}>
            <div className="text-xs text-gray-600 mb-1">{label}</div>
            <div className={`font-bold ${isNumber ? 'text-center' : 'text-right'} ${highlight ? 'text-green-700' : danger ? 'text-red-700' : 'text-[#2a2e30]'}`}>
                {value}
            </div>
        </div>
    );
};

const SummaryRow = ({ label, value, bold, highlight, success, danger }) => {
    let textColor = 'text-gray-700';
    if (highlight) textColor = 'text-[#FF5733]';
    if (success) textColor = 'text-green-700';
    if (danger) textColor = 'text-red-700';

    return (
        <div className="flex justify-between items-center text-sm">
            <span className={bold ? 'font-semibold' : ''}>{label}:</span>
            <span className={`${bold ? 'font-bold' : ''} ${textColor} text-right`}>{value}</span>
        </div>
    );
};

const TransactionSection = ({ title, transactions }) => {
    return (
        <div>
            <h4 className="text-sm font-bold text-[#2a2e30] mb-2">{title}</h4>
            <div className="space-y-1 max-h-60 overflow-y-auto">
                {transactions.map((trans, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs bg-gray-50 p-2 rounded">
                        <div className="flex-1">
                            <div className="font-semibold">{trans.doc_no}</div>
                            <div className="text-gray-600">{trans.short_desc}</div>
                            <div className="text-gray-500">
                                {new Date(trans.date).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                })}
                            </div>
                        </div>
                        <div className="text-right font-bold">
                            {new Intl.NumberFormat('en-KE', {
                                style: 'currency',
                                currency: 'KES',
                                minimumFractionDigits: 2
                            }).format(parseFloat(trans.debit || 0) - parseFloat(trans.credit || 0))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CarFinancialDetailCards;
